import get from "lodash/get";
import set from "lodash/set";
import values from "lodash/values";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isFunction from "lodash/isFunction";
import { safeEval, compactJoin } from "utils/helpers";
import { CONTRACT_TYPES } from "utils/constants";
import moment from "moment";

export const emailRegex =
  "^[a-z0-9]+([-_a-z0-9]+){0,}([\\.|+|~][-_a-z0-9]+){0,}@((?:[-a-z0-9]+\\.)+[a-z]{2,63})$";

const required = ({ formValue, message }) => {
  return formValue || typeof formValue === "number" ? undefined : message;
};

const empty = ({ formValue, message }) => {
  return isEmpty(formValue) ? undefined : message;
};

const field_equal = ({ formValue, formValues, message, fromField }) => {
  return get(formValues, fromField) !== formValue ? message : undefined;
};

const email_verification = ({ fromField, message, ...props }) => {
  return !get(props, fromField) ? message : undefined;
};

const email = props =>
  regex({
    ...props,
    value: emailRegex
  });

const email_and_mobile = props => {
  let isAtPresent;
  const contactNumberRegex =
    props?.appSettings?.global_config?.validations?.new_number_regex;
  if (!isEmpty(props?.formValues?.email)) {
    isAtPresent = props?.formValues?.email.includes("@") ? true : false;
  }
  return isAtPresent
    ? regex({ ...props, value: emailRegex })
    : regex({ ...props, value: contactNumberRegex });
};

export const unique_email = ({ formValues, message }) => {
  return formValues &&
    new Set(values(formValues).map(email => email?.toString().toLowerCase()))
      .size === 1
    ? message
    : undefined;
};

const kanji_name = props =>
  regex({
    ...props,
    value: "^[一-龯]+$"
  });

const kanji_name_with_space = props =>
  regex({
    ...props,
    value: "^[一-龯]+(([',. -][一-龯])?[一-龯]*)*$"
  });

const kanji_name_with_alphabets_and_space = props =>
  regex({
    ...props,
    value: "^[一-龯a-zA-Z]+(([',. -][一-龯a-zA-Z])?[一-龯a-zA-Z]*)*$"
  });

const kanji_name_with_alphabets = props =>
  regex({
    ...props,
    value: "^[一-龯a-zA-Z]+$"
  });

const kana_name_with_alphabets = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ンa-zA-Z]+$"
  });

const kana_name = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ン]+$"
  });

const kana_name_with_space = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ン]+(([',. -][ぁ-んァ-ン])?[ぁ-んァ-ン]*)*$"
  });

const kana_name_with_alphabets_and_space = props =>
  regex({
    ...props,
    value:
      "^[ぁ-んァ-ンa-zA-Z]+(([',. -][ぁ-んァ-ンa-zA-Z])?[ぁ-んァ-ンa-zA-Z]*)*$"
  });

const alphabets_and_space = props =>
  regex({
    ...props,
    value: "^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
  });

const alphabets_accents_and_space = props =>
  regex({
    ...props,
    value: "^[a-zA-ZÀ-ɏḀ-ỿ]+(([', -][a-zA-ZÀ-ɏḀ-ỿ ])?[a-zA-ZÀ-ɏḀ-ỿ]*)*$"
  });
// For first/middle/last names
const alphabets_and_special_chars = props =>
  regex({
    ...props,
    value: "^[-a-zA-Z()/\\\\.,~^\"'‘’` ]*$"
  });

const min_value = ({ formValue, value, message }) => {
  return formValue && formValue < value ? message : undefined;
};

const location_error = ({ formValue, value, ...data }) => {
  const zipCodeLength = get(data?.formValues, data?.field)?.length;
  const checkMaxLength = get(data, "checkMaxLength", true);
  if (get(data?.formValues, `${data?.field}_zero_result`) === "ZERO_RESULTS") {
    return data?.message;
  }
  if (
    zipCodeLength > 0 &&
    zipCodeLength < get(data, "maxLength", 6) &&
    checkMaxLength
  ) {
    if (data?.formValues?.activeField !== data?.field) {
      return data?.message;
    } else {
      return " ";
    }
  } else {
    return undefined;
  }
};

const regex = ({
  formValue,
  message,
  value,
  regex,
  global,
  formValues,
  ...props
}) => {
  const regexValue = global
    ? safeEval(get, formValues, value, true, props)
    : value;
  return formValue && !new RegExp(regexValue || regex, "i").test(formValue)
    ? message
    : undefined;
};

const port_in_regex = ({
  formValue,
  message,
  value,
  regex,
  global,
  formValues,
  ...props
}) => {
  const regexValue = global
    ? safeEval(get, formValues, value, true, props)
    : value;
  const { resetPortInNumberValidation } = props;

  const isValid = new RegExp(regexValue || regex, "i").test(formValue);
  !isValid &&
    isFunction(resetPortInNumberValidation) &&
    resetPortInNumberValidation();
  return formValue && !isValid ? message : undefined;
};

const length_between = ({
  formValues,
  formValue,
  message,
  min_length,
  max_length
}) => {
  const minLength = safeEval(get, formValues, min_length);
  const maxLength = safeEval(get, formValues, max_length);
  const formValueLength = formValue ? formValue.length : 0;
  if (minLength && formValueLength < minLength) {
    return message;
  } else if (maxLength && formValueLength > maxLength) {
    return message;
  } else {
    return undefined;
  }
};

const port_in_number = async ({ formValue, field, regex, message, props }) => {
  const prevFieldValue = get(props, ["formValues", field]);
  const orderRef = get(props, "orderRef", "");
  if (regex) {
    const validationRegex = RegExp(regex);
    const {
      validatePortInNumber,
      resetPortInNumberValidation,
      portInNumberValidation = {}
    } = props;

    if (validationRegex.test(formValue)) {
      if (prevFieldValue !== formValue) {
        isFunction(validatePortInNumber) &&
          validatePortInNumber(formValue, orderRef);
        return undefined;
      } else {
        const { valid, message: validationMessage } = portInNumberValidation;
        return !valid ? validationMessage : undefined;
      }
    } else {
      isFunction(resetPortInNumberValidation) && resetPortInNumberValidation();
      return message;
    }
  }
};

const port_in_number_with_new_number = async ({
  formValue,
  field,
  regex,
  message,
  props
}) => {
  const numberType = get(props, ["formValues", "number_type"]);
  if (numberType === CONTRACT_TYPES.NEW) return;

  const prevFieldValue = get(props, ["formValues", field]);
  if (!!formValue && regex) {
    const splitRegex = regex.split("/");
    const validationRegex = RegExp(splitRegex[1], splitRegex[2]);
    const {
      validatePortInNumber,
      resetPortInNumberValidation,
      portInNumberValidation = {}
    } = props;

    if (validationRegex.test(formValue)) {
      if (prevFieldValue !== formValue) {
        isFunction(validatePortInNumber) && validatePortInNumber(formValue);
        return undefined;
      } else {
        const { valid, message: validationMessage } = portInNumberValidation;
        return !valid ? validationMessage : undefined;
      }
    } else {
      isFunction(resetPortInNumberValidation) && resetPortInNumberValidation();
      return message;
    }
  }
};

const port_in_number_with_donor_code = ({
  formValue,
  message,
  field,
  props,
  dependencyField
}) => {
  let mandatoryField = dependencyField || field;
  const numberType = get(props, ["formValues", "number_type"]);
  if (numberType === CONTRACT_TYPES.NEW) return;
  if (formValue) {
    const { validatePortInNumber, resetPortInNumberValidation } = props;
    if (!isEmpty(get(props, ["formValues", mandatoryField]))) {
      isFunction(validatePortInNumber) &&
        validatePortInNumber(get(props, "formValues"));
      return undefined;
    } else {
      isFunction(resetPortInNumberValidation) && resetPortInNumberValidation();
      // return message;
    }
  }
};

const postal_code = async ({ formValue, field, regex, message, props }) => {
  const prevFieldValue = get(props, ["formValues", field]);
  if (!prevFieldValue && !formValue) {
    return undefined;
  }

  if (regex) {
    const validationRegex = RegExp(regex);
    const {
      validatePostalCode,
      resetPostalCodeValidation,
      postalCodeValidation = {}
    } = props;

    if (validationRegex.test(formValue)) {
      if (prevFieldValue !== formValue) {
        isFunction(validatePostalCode) && validatePostalCode(formValue, field);
        return undefined;
      } else {
        const { valid } = get(postalCodeValidation, [field], {});
        if (isNil(valid)) return undefined;

        return !isEmpty(postalCodeValidation) && !valid ? message : undefined;
      }
    } else {
      isFunction(resetPostalCodeValidation) && resetPostalCodeValidation(field);
      return message;
    }
  }
};

export const nric_singapore = ({ formValue, regex, message }) => {
  let i;
  let theAlpha;
  let weight;
  let parsedStr = formValue;
  if (parsedStr.length !== 9) {
    return message;
  }
  parsedStr = parsedStr.toUpperCase();
  i = 0;
  const icArray = [];
  i = 0;
  while (i < 9) {
    icArray[i] = parsedStr.charAt(i);
    i++;
  }
  icArray[1] = parseInt(icArray[1], 10) * 2;
  icArray[2] = parseInt(icArray[2], 10) * 7;
  icArray[3] = parseInt(icArray[3], 10) * 6;
  icArray[4] = parseInt(icArray[4], 10) * 5;
  icArray[5] = parseInt(icArray[5], 10) * 4;
  icArray[6] = parseInt(icArray[6], 10) * 3;
  icArray[7] = parseInt(icArray[7], 10) * 2;
  weight = 0;
  i = 1;
  while (i < 8) {
    weight += icArray[i];
    i++;
  }
  const offset = icArray[0] === "T" || icArray[0] === "G" ? 4 : 0;
  const temp = (offset + weight) % 11;
  const st = ["J", "Z", "I", "H", "G", "F", "E", "D", "C", "B", "A"];
  const fg = ["X", "W", "U", "T", "R", "Q", "P", "N", "M", "L", "K"];
  theAlpha = 0;
  if (icArray[0] === "S" || icArray[0] === "T") {
    theAlpha = st[temp];
  } else if (icArray[0] === "F" || icArray[0] === "G") {
    theAlpha = fg[temp];
  }
  if (icArray[8] === theAlpha) {
    return null;
  }
  return message;
};

const national_id_taiwan = ({ formValue, regex, message }) => {
  if (formValue.length !== 10 || (regex && !formValue.match(regex))) {
    return message;
  }
  const taiwanLocationCode = {
    A: "10",
    B: "11",
    C: "12",
    D: "13",
    E: "14",
    F: "15",
    G: "16",
    H: "17",
    I: "34",
    J: "18",
    K: "19",
    L: "20",
    M: "21",
    N: "22",
    O: "35",
    P: "23",
    Q: "24",
    R: "25",
    S: "26",
    T: "27",
    U: "28",
    V: "29",
    W: "32",
    X: "30",
    Y: "31",
    Z: "33"
  };

  const validateNationalId = (location, nationalId) => {
    const nationalIdLength = nationalId.length - 1;
    let totalSum =
      Number(nationalId[nationalId.length - 1]) +
      (Number(location[0]) * 1 + Number(location[1]) * 9);
    for (let i = 1; i <= 8; i++) {
      totalSum += Number(nationalId[nationalIdLength - i]) * i;
    }
    return totalSum % 10;
  };

  const nationalId = formValue.split("");
  const location = taiwanLocationCode[nationalId[0]];
  return validateNationalId(location, nationalId) === 0 ? null : message;
};

const future_date = ({ message, formValues, prefix }) => {
  const day = get(
    formValues,
    prefix ? `${compactJoin([prefix, "dobDay"], "_")}.value` : "dobDay.value"
  );
  const month = get(
    formValues,
    prefix
      ? `${compactJoin([prefix, "dobMonth"], "_")}.value`
      : "dobMonth.value"
  );
  const year = get(
    formValues,
    prefix ? `${compactJoin([prefix, "dobYear"], "_")}.value` : "dobYear.value"
  );
  const date = moment(`${day}-${month}-${year}`, "DD-MM-YYYY");
  const today = moment();

  if (date.diff(today) <= 0) {
    return message;
  }
  return undefined;
};

const mapping = {
  presence: required,
  empty,
  unique_email,
  regex,
  port_in_regex,
  email,
  alphabets_and_space,
  alphabets_accents_and_space,
  alphabets_and_special_chars,
  min_value,
  length_between,
  nric_singapore,
  national_id_taiwan,
  kanji_name,
  kanji_name_with_space,
  kanji_name_with_alphabets_and_space,
  kana_name,
  kana_name_with_alphabets_and_space,
  kana_name_with_space,
  kanji_name_with_alphabets,
  kana_name_with_alphabets,
  location_error,
  field_equal,
  port_in_number_with_donor_code,
  email_verification,
  email_and_mobile
};

const asyncMapping = {
  port_in_number_with_new_number,
  port_in_number,
  postal_code,
  future_date
};

const validate = (formValues, props) => {
  const errors = {};
  const { validations = [], appSettings = {} } = props.formValidations || {};

  validations.forEach(validation => {
    const { type, field, condition, async } = validation;
    let formValue = get(formValues, field);
    const validationNeeded = safeEval(get, formValues, condition, true, props);

    if (get(errors, field, false) || !validationNeeded || async) {
      return;
    }

    // custom validation lookup from various shared configuration files

    if ("lookup" in validation) {
      let sharedValidations = get(appSettings, validation.lookup);
      if ("lookup_type" in validation) {
        const lookupType = safeEval(
          get,
          formValues,
          validation.lookup_type,
          {},
          props
        );
        sharedValidations = sharedValidations[lookupType] || {};
      }
      const requiredInConfig = get(sharedValidations, "required");
      const errorMessage = get(
        sharedValidations,
        "error_msg",
        "error_cannot_be_blank"
      );

      if (field === "age" || field === "other_user_age") {
        set(
          errors,
          field,
          mapping["min_value"]({
            formValue,
            value: sharedValidations.value,
            message: errorMessage
          })
        );
      } else if (
        field.match(/^((contact|alternate|temporary_contact)_number|icc_id)$/)
      ) {
        set(
          errors,
          field,
          mapping["regex"]({
            formValue,
            value:
              props[sharedValidations.validation_regex] ||
              sharedValidations.validation_regex,
            message: errorMessage
          })
        );
      } else if (field === "nric") {
        const docType = get(formValues, "doc_type.value", {});
        validation = sharedValidations[docType] || {};

        set(
          errors,
          field,
          mapping["nric_singapore"]({
            formValue,
            regex: validation.validation_regex,
            message: validation.error_msg
          })
        );
      } else if (field.match(/^(billing|delivery)_/)) {
        const field_key = field.replace(/^(billing|delivery)_/, "");
        const validation = get(sharedValidations, `fields.${field_key}`, {});
        const required = get(validation, "required", false);

        required &&
          set(
            errors,
            field,
            mapping["presence"]({
              formValue,
              message: validation.error_msg
            })
          );
      } else {
        let validationType = sharedValidations.validation_type || type;
        validationType =
          !validationType && sharedValidations.validation_regex
            ? "regex"
            : validationType;
        const validationFunction = mapping[validationType];
        if (isFunction(validationFunction)) {
          set(
            errors,
            field,
            validationFunction({
              regex: sharedValidations.validation_regex,
              message: errorMessage,
              formValues,
              formValue,
              props,
              ...validation
            })
          );
        }
      }

      if (!errors[field] && requiredInConfig) {
        set(
          errors,
          field,
          mapping["presence"]({
            formValue,
            message: errorMessage
          })
        );
      }
    } else {
      if (isFunction(mapping[type])) {
        set(
          errors,
          field,
          mapping[type]({ formValues, formValue, ...validation, ...props })
        );
      }
    }
  });

  return errors;
};

async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

export const asyncValidate = async (formValues, dispatch, props) => {
  return new Promise(resolve => resolve()).then(async () => {
    const errors = {};
    const { validations = [], appSettings = {} } = props.formValidations || {};
    await asyncForEach(validations, async validation => {
      const {
        type,
        field,
        condition,
        async,
        message,
        dependencyField
      } = validation;
      let formValue = get(formValues, field);
      const validationNeeded = safeEval(
        get,
        formValues,
        condition,
        true,
        props
      );

      if (get(errors, field, false) || !validationNeeded || !async) {
        return;
      }

      if (isFunction(asyncMapping[type])) {
        const asyncMappingProps = {
          field,
          formValues,
          formValue,
          dispatch,
          props,
          ...validation
        };

        if ("lookup" in validation) {
          const sharedValidations = get(appSettings, validation.lookup);
          asyncMappingProps["regex"] =
            sharedValidations.regex ||
            sharedValidations.validation_regex ||
            sharedValidations;
          asyncMappingProps["message"] = sharedValidations.error_msg || message;
          asyncMappingProps["dependencyField"] = dependencyField || field;
        }

        const errorMessage = await asyncMapping[type](asyncMappingProps);
        if (errorMessage) {
          errors[field] = errorMessage;
        }
      }
    });
    if (!isEmpty(errors)) {
      throw errors;
    }
  });
};

export default validate;
